"use client";

import { useState } from "react";
import { Clock, Tag, MessageCircle, X, Loader2 } from "lucide-react";
import dayjs from "dayjs";
import { useIsMobile } from "@/app/_hooks/use-is-moble";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/app/_components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/app/_components/ui/drawer";

interface Promotion {
  id: string;
  title: string;
  description: string;
  tag: string;
  duration: Date | string;
  image: string;
}

interface PromotionDetailProps {
  promo: Promotion | null;
  open: boolean;
  onClose: () => void;
  onStartConversation: (title: string) => Promise<void>;
}

function PromotionDetailContent({
  promo,
  onClose,
  onStartConversation,
}: {
  promo: Promotion;
  onClose: () => void;
  onStartConversation: (title: string) => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const isNew = promo.tag === "New";

  const imageUrl = promo.image.startsWith("http")
    ? promo.image
    : `https://res.cloudinary.com/${process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME}/image/upload/${promo.image}`;

  const handleStart = async () => {
    setLoading(true);
    try {
      await onStartConversation(promo.title);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-0">
      {/* Image */}
      <div
        className="h-[200px] w-full bg-cover bg-center relative rounded-t-lg overflow-hidden"
        style={{ backgroundImage: `url('${imageUrl}')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <span
          className={`absolute top-4 left-4 text-[11px] font-semibold px-3 py-1 rounded-full flex items-center gap-1.5 ${
            isNew ? "bg-primary text-white" : "bg-amber-400 text-amber-900"
          }`}
        >
          {isNew ? <Tag size={11} /> : <Clock size={11} />}
          {promo.tag}
        </span>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-foreground">{promo.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{promo.description}</p>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock size={12} />
          <span>Valid until {dayjs(promo.duration).format("MMMM D, YYYY")}</span>
        </div>

        <button
          onClick={handleStart}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-primary text-white font-medium py-3 rounded-[10px] hover:opacity-90 transition-opacity disabled:opacity-50 mt-1"
        >
          {loading ? (
            <><Loader2 size={15} className="animate-spin" /> Setting up chat...</>
          ) : (
            <><MessageCircle size={15} /> Start Conversation</>
          )}
        </button>
        <p className="text-[10px] text-center text-muted-foreground">
          A service request chat will be opened for this promotion
        </p>
      </div>
    </div>
  );
}

export default function PromotionDetail({
  promo,
  open,
  onClose,
  onStartConversation,
}: PromotionDetailProps) {
  const isMobile = useIsMobile();

  if (!promo) return null;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(v) => !v && onClose()}>
        <DrawerContent className="p-0 overflow-hidden">
          <DrawerHeader className="sr-only">
            <DrawerTitle>{promo.title}</DrawerTitle>
          </DrawerHeader>
          <PromotionDetailContent
            promo={promo}
            onClose={onClose}
            onStartConversation={onStartConversation}
          />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="p-0 max-w-md overflow-hidden gap-0">
        <DialogHeader className="sr-only">
          <DialogTitle>{promo.title}</DialogTitle>
        </DialogHeader>
        <PromotionDetailContent
          promo={promo}
          onClose={onClose}
          onStartConversation={onStartConversation}
        />
      </DialogContent>
    </Dialog>
  );
}
